/**
 * Video player: YouTube IFrame API for youtube videos, a plain <video> for everything else.
 * Exposes seek / play / getCurrentTime through a ref and reports playback time through `onTime`.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import styles from './player.module.css';

export interface PlayerHandle {
  seekTo: (sec: number) => void;
  getCurrentTime: () => number;
  play: () => void;
}

declare global {
  interface Window {
    YT?: {
      Player: new (el: HTMLElement, opts: Record<string, unknown>) => YTPlayer;
      PlayerState?: Record<string, number>;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}
interface YTPlayer {
  seekTo: (s: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  playVideo: () => void;
  destroy: () => void;
}

let ytReady: Promise<void> | null = null;
function loadYouTubeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.YT?.Player) return Promise.resolve();
  if (ytReady) return ytReady;
  ytReady = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.async = true;
    document.head.appendChild(s);
  });
  return ytReady;
}

export interface PlayerProps {
  platform: string;
  externalId: string;
  url: string;
  thumbnail?: string | null;
  /** Accessible name of the player region. */
  title?: string;
  /** Called every `pollMs` with the current playback position. */
  onTime?: (sec: number) => void;
  pollMs?: number;
  className?: string;
}

export const Player = forwardRef<PlayerHandle, PlayerProps>(function Player(
  { platform, externalId, url, thumbnail, title, onTime, pollMs = 250, className },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const ytRef = useRef<YTPlayer | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;
  const isYt = platform === 'youtube';

  useEffect(() => {
    if (!isYt || !hostRef.current) return;
    let cancelled = false;
    const host = hostRef.current;
    const mount = document.createElement('div');
    host.appendChild(mount);
    void loadYouTubeApi().then(() => {
      if (cancelled || !window.YT) return;
      ytRef.current = new window.YT.Player(mount, {
        videoId: externalId,
        width: '100%',
        height: '100%',
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1, origin: window.location.origin },
      });
    });
    return () => {
      cancelled = true;
      try {
        ytRef.current?.destroy();
      } catch {
        /* ignore */
      }
      ytRef.current = null;
      if (mount.parentNode === host) host.removeChild(mount);
    };
  }, [isYt, externalId]);

  const getCurrentTime = () => {
    try {
      if (isYt) return ytRef.current?.getCurrentTime?.() ?? 0;
      return videoRef.current?.currentTime ?? 0;
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    if (!onTimeRef.current) return;
    const t = setInterval(() => onTimeRef.current?.(getCurrentTime()), pollMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYt, pollMs]);

  useImperativeHandle(
    ref,
    () => ({
      seekTo: (sec) => {
        if (isYt) {
          try {
            ytRef.current?.seekTo(sec, true);
            ytRef.current?.playVideo();
          } catch {
            /* player not ready yet */
          }
        } else if (videoRef.current) {
          videoRef.current.currentTime = sec;
          void videoRef.current.play().catch(() => undefined);
        }
      },
      getCurrentTime,
      play: () => {
        if (isYt) ytRef.current?.playVideo();
        else void videoRef.current?.play().catch(() => undefined);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isYt],
  );

  return (
    <div
      className={[styles.frame, className].filter(Boolean).join(' ')}
      role="region"
      aria-label={title ?? 'Video player'}
    >
      {isYt ? (
        <div ref={hostRef} className={styles.host} />
      ) : (
        <video ref={videoRef} src={url} poster={thumbnail ?? undefined} controls className={styles.video} />
      )}
    </div>
  );
});
